import {Options, TableDataRequest} from '@fl/cmsch-fe-library';
import {Opt} from 'ts-opt';

import {AuthDiscriminatorDetail} from 'api/gen/AuthDiscriminatorDetail';
import {BasicRole} from 'api/gen/BasicRole';
import {ColumnHeader} from 'api/gen/ColumnHeader';
import {Farm} from 'api/gen/Farm';
import {OrganizationCenter} from 'api/gen/OrganizationCenter';
import {PagedModelAssociationRole} from 'api/gen/PagedModelAssociationRole';
import {PagedModelBasicRole} from 'api/gen/PagedModelBasicRole';
import {PagedModelBreederRole} from 'api/gen/PagedModelBreederRole';
import {PagedModelOrganizationRole} from 'api/gen/PagedModelOrganizationRole';
import {PagedModelOtherSubjectRole} from 'api/gen/PagedModelOtherSubjectRole';
import {PagedModelPermissionDetail} from 'api/gen/PagedModelPermissionDetail';
import {PagedModelRolePermission} from 'api/gen/PagedModelRolePermission';
import {PagedModelRoleView} from 'api/gen/PagedModelRoleView';
import {PagedModelUserProfileContact} from 'api/gen/PagedModelUserProfileContact';
import {PagedModelUserSubjectView} from 'api/gen/PagedModelUserSubjectView';
import {PagedModelUserView} from 'api/gen/PagedModelUserView';
import {PermissionDetail} from 'api/gen/PermissionDetail';
import {RoleDetail} from 'api/gen/RoleDetail';
import {RolePermission} from 'api/gen/RolePermission';
import {RoleView} from 'api/gen/RoleView';
import {User} from 'api/gen/User';
import {UserDetail} from 'api/gen/UserDetail';
import {UserProfileAssociationRole} from 'api/gen/UserProfileAssociationRole';
import {UserProfileBreederRoleFarms} from 'api/gen/UserProfileBreederRoleFarms';
import {UserProfileOrganizationRoleFarmsCenters} from 'api/gen/UserProfileOrganizationRoleFarmsCenters';
import {UserProfileOtherSubjectRole} from 'api/gen/UserProfileOtherSubjectRole';
import {ManagementOrganizationsPage, ManagementOrganizationsTableType} from 'app/admin/types/management-organizations';
import {AssociationRoleTableType} from 'app/user-detail/types/association-role-table-type';
import {BreederRoleTableType} from 'app/user-detail/types/breeder-role-table-type';
import {MemberOfSubjectsTableType} from 'app/user-detail/types/member-of-subjects-table-type';
import {OrganizationTableType} from 'app/user-detail/types/organization-table-type';
import {OtherSubjectRoleTableType} from 'app/user-detail/types/other-subject-role-table-type';
import {UserContactsTableType} from 'app/user-detail/types/user-contacts-table-type';

import {AppInfoType} from '../types';
import {AdminLoadingId} from '../types/admin-loading-id';
import {ManagementAssociationsPage, ManagementAssociationsTableType} from '../types/management-associations';
import {ManagementBreedersPage, ManagementBreedersTableType} from '../types/management-breeders';
import {ManagementOtherSubjectsPage, ManagementOtherSubjectsTableType} from '../types/management-other-subjects';
import {CreateOrEditMode} from '../types/permission-form-mode';

/** Action creators for the admin module. */
export const adminAction = {
    getAppInfo: () => ({
        type: 'admin/GET_APP_INFO',
    }) as const,
    getAppInfoSuccess: (appInfo: AppInfoType) => ({
        type: 'admin/GET_APP_INFO_SUCCESS',
        payload: {appInfo},
    }) as const,
    createPermission: () => ({
        type: 'admin/CREATE_PERMISSION',
    }) as const,
    createRole: () => ({
        type: 'admin/CREATE_ROLE',
    }) as const,
    updatePermission: (permissionId: number) => ({
        type: 'admin/UPDATE_PERMISSION',
        payload: {permissionId},
    }) as const,
    updateRole: (roleId: number) => ({
        type: 'admin/UPDATE_ROLE',
        payload: {roleId},
    }) as const,
    updateUserMembership: () => ({
        type: 'admin/UPDATE_USER_MEMBERSHIP',
    }) as const,
    updateAssociationRoles: () => ({
        type: 'admin/UPDATE_ASSOCIATION_ROLES',
    }) as const,
    updateOtherSubjects: () => ({
        type: 'admin/UPDATE_OTHER_SUBJECTS',
    }) as const,
    createOtherSubjects: () => ({
        type: 'admin/CREATE_OTHER_SUBJECTS',
    }) as const,
    createAssociationRoles: () => ({
        type: 'admin/CREATE_ASSOCIATION_ROLES',
    }) as const,
    createOrganization: () => ({
        type: 'admin/CREATE_ORGANIZATION',
    }) as const,
    updateOrganization: () => ({
        type: 'admin/UPDATE_ORGANIZATION',
    }) as const,
    createUserMembership: () => ({
        type: 'admin/CREATE_USER_MEMBERSHIP',
    }) as const,
    getMemberOfSubjects: (
        tableDataRequest: Opt<TableDataRequest<MemberOfSubjectsTableType>>,
        userProfileId: number,
    ) => ({
        type: 'admin/GET_MEMBER_OF_SUBJECTS',
        payload: {tableDataRequest, userProfileId},
    }) as const,
    getMemberOfSubjectsSuccess: (rolesPage: Opt<PagedModelUserSubjectView>) => ({
        type: 'admin/GET_MEMBER_OF_SUBJECTS_SUCCESS',
        payload: rolesPage,
    }) as const,
    getUserContacts: (
        tableDataRequest: Opt<TableDataRequest<UserContactsTableType>>,
        userProfileId: number,
    ) => ({
        type: 'admin/GET_USER_CONTACTS',
        payload: {tableDataRequest, userProfileId},
    }) as const,
    getUserContactsSuccess: (rolesPage: Opt<PagedModelUserProfileContact>) => ({
        type: 'admin/GET_USER_CONTACTS_SUCCESS',
        payload: rolesPage,
    }) as const,
    validatePermission: (
        tableRecord: PermissionDetail | undefined,
        mode: CreateOrEditMode,
    ) => ({
        type: 'admin/VALIDATE_PERMISSION',
        payload: {tableRecord, mode},
    }) as const,
    validateRole: (tableRecord: RoleView | undefined, mode: CreateOrEditMode) => ({
        type: 'admin/VALIDATE_ROLE',
        payload: {tableRecord, mode},
    }) as const,
    getSubordinateAssignedRoles: (tableDataRequest: Opt<TableDataRequest<BasicRole>>, parentRoleId: number) => ({
        type: 'admin/GET_SUBORDINATE_ASSIGNED_ROLES',
        payload: {tableDataRequest, roleId: parentRoleId},
    }) as const,
    getSubordinateAssignedRolesSuccess: (rolesPage: Opt<PagedModelBasicRole>) => ({
        type: 'admin/GET_SUBORDINATE_ASSIGNED_ROLES_SUCCESS',
        payload: rolesPage,
    }) as const,
    getSubordinateUnassignedRoles: (tableDataRequest: Opt<TableDataRequest<BasicRole>>, parentRoleId: number) => ({
        type: 'admin/GET_SUBORDINATE_UNASSIGNED_ROLES',
        payload: {tableDataRequest, roleId: parentRoleId},
    }) as const,
    getSubordinateUnassignedRolesSuccess: (rolesPage: Opt<PagedModelBasicRole>) => ({
        type: 'admin/GET_SUBORDINATE_UNASSIGNED_ROLES_SUCCESS',
        payload: rolesPage,
    }) as const,
    getUsersSuccess: (usersPage: Opt<PagedModelUserView>) => ({
        type: 'admin/GET_USERS_SUCCESS',
        payload: usersPage,
    }) as const,
    getRoles: (tableDataRequest: Opt<TableDataRequest<RoleView>>) => ({
        type: 'admin/GET_ROLES',
        payload: {tableDataRequest},
    }) as const,
    getRolesSuccess: (rolesPage: Opt<PagedModelRoleView>) => ({
        type: 'admin/GET_ROLES_SUCCESS',
        payload: rolesPage,
    }) as const,
    getAssociations: (tableDataRequest: Opt<TableDataRequest<AssociationRoleTableType>>, userProfileId: number) => ({
        type: 'admin/GET_USER_ASSOCIATIONS',
        payload: {tableDataRequest, userProfileId},
    }) as const,
    getAssociationsSuccess: (associationsPage: Opt<PagedModelAssociationRole>) => ({
        type: 'admin/GET_USER_ASSOCIATIONS_SUCCESS',
        payload: associationsPage,
    }) as const,
    getOrganizations: (
        tableDataRequest: Opt<TableDataRequest<OrganizationTableType>>,
        userProfileId: number,
    ) => ({
        type: 'admin/GET_ORGANIZATIONS',
        payload: {tableDataRequest, userProfileId},
    }) as const,
    getOrganizationsSuccess: (organizationsPage: Opt<PagedModelOrganizationRole>) => ({
        type: 'admin/GET_ORGANIZATIONS_SUCCESS',
        payload: organizationsPage,
    }) as const,
    getUserBreeders: (
        tableDataRequest: Opt<TableDataRequest<BreederRoleTableType>>,
        userProfileId: number,
    ) => ({
        type: 'admin/GET_USER_BREEDERS',
        payload: {tableDataRequest, userProfileId},
    }) as const,
    getUserBreedersSuccess: (authorizedAssociationsPage: Opt<PagedModelBreederRole>) => ({
        type: 'admin/GET_USER_BREEDERS_SUCCESS',
        payload: authorizedAssociationsPage,
    }) as const,
    getOtherSubjectRoles: (request: Opt<TableDataRequest<OtherSubjectRoleTableType>>, userProfileId: number) => ({
        type: 'admin/GET_USER_OTHER_SUBJECT_ROLES',
        payload: {request, userProfileId},
    }) as const,
    getOtherSubjectRolesSuccess: (otherSubjectRolesPage: Opt<PagedModelOtherSubjectRole>) => ({
        type: 'admin/GET_USER_OTHER_SUBJECT_ROLES_SUCCESS',
        payload: otherSubjectRolesPage,
    }) as const,
    getRole: (roleId: number) => ({
        type: 'admin/GET_ROLE',
        payload: {roleId},
    }) as const,
    getRoleSuccess: (role: RoleDetail) => ({
        type: 'admin/GET_ROLE_SUCCESS',
        payload: role,
    }) as const,
    clearRole: () => ({
        type: 'admin/CLEAR_ROLE',
    }) as const,
    getUser: (userId: number) => ({
        type: 'admin/GET_USER',
        payload: {userId},
    }) as const,
    clearUser: () => ({
        type: 'admin/CLEAR_USER',
    }) as const,
    getUserSuccess: (user: UserDetail) => ({
        type: 'admin/GET_USER_SUCCESS',
        payload: user,
    }) as const,
    getRolePermissions: (roleId: number, tableDataRequest: Opt<TableDataRequest<RolePermission>>) => ({
        type: 'admin/GET_ROLE_PERMISSIONS',
        payload: {roleId, tableDataRequest},
    }) as const,
    getRolePermissionsSuccess: (rolePermissionsPage: Opt<PagedModelRolePermission>) => ({
        type: 'admin/GET_ROLE_PERMISSIONS_SUCCESS',
        payload: rolePermissionsPage,
    }) as const,
    getRoleUnassignedPermissions: (roleId: number, tableDataRequest: Opt<TableDataRequest<RolePermission>>) => ({
        type: 'admin/GET_ROLE_UNASSIGNED_PERMISSIONS',
        payload: {roleId, tableDataRequest},
    }) as const,
    getRoleUnassignedPermissionsSuccess: (roleUnassignedPermissionsPage: Opt<PagedModelRolePermission>) => ({
        type: 'admin/GET_ROLE_UNASSIGNED_PERMISSIONS_SUCCESS',
        payload: roleUnassignedPermissionsPage,
    }) as const,
    changeSubordinateRole: (parentRoleId: number, subordinateRoleId: number, method: 'add' | 'remove') => ({
        type: 'admin/CHANGE_SUBORDINATE_ROLE',
        payload: {parentRoleId, subordinateRoleId, method},
    }) as const,
    removePermissionFromRole: (roleId: number, permissionId: number) => ({
        type: 'admin/REMOVE_PERMISSION_FROM_ROLE',
        payload: {roleId, permissionId},
    }) as const,
    addPermissionToRole: (roleId: number, permissionId: number) => ({
        type: 'admin/ADD_PERMISSION_TO_ROLE',
        payload: {roleId, permissionId},
    }) as const,
    getPermissionDetails: (tableDataRequest: Opt<TableDataRequest<PermissionDetail>>) => ({
        type: 'admin/GET_PERMISSION_DETAILS',
        payload: {tableDataRequest},
    }) as const,
    getPermissionDetailsSuccess: (permissionsPage: Opt<PagedModelPermissionDetail>) => ({
        type: 'admin/GET_PERMISSION_DETAILS_SUCCESS',
        payload: permissionsPage,
    }) as const,
    deletePermission: (permissionId: number) => ({
        type: 'admin/DELETE_PERMISSION',
        payload: {permissionId},
    }) as const,
    deleteRole: (roleId: number) => ({
        type: 'admin/DELETE_ROLE',
        payload: {roleId},
    }) as const,
    setEditedPermissionId: (permissionId: number | null) => ({
        type: 'admin/SET_EDITED_PERMISSION_ID',
        payload: permissionId,
    }) as const,
    setEditedRoleId: (permissionId: number | null) => ({
        type: 'admin/SET_EDITED_ROLE_ID',
        payload: permissionId,
    }) as const,
    setPermissionCreateVisibility: (visible: boolean) => ({
        type: 'admin/SET_PERMISSION_CREATE_VISIBILITY',
        payload: visible,
    }) as const,
    setRoleCreateModalVisibility: (visible: boolean) => ({
        type: 'admin/SET_ROLE_CREATE_VISIBILITY',
        payload: visible,
    }) as const,
    deleteUserContact: (userProfileId: number, userContactId: number) => ({
        type: 'admin/DELETE_USER_CONTACT',
        payload: {userProfileId, userContactId},
    }) as const,
    updateUserContact: () => ({
        type: 'admin/UPDATE_USER_CONTACT',
    }) as const,
    createUserContact: () => ({
        type: 'admin/CREATE_USER_CONTACT',
    }) as const,
    updateBreeder: () => ({
        type: 'admin/UPDATE_BREEDER',
    }) as const,
    createBreeder: () => ({
        type: 'admin/CREATE_BREEDER',
    }) as const,
    setLoading: (id: AdminLoadingId, loading: boolean) => ({
        type: 'admin/SET_LOADING',
        payload: {id, loading},
    }) as const,
    getManagementBreedersTableData: (
        tableDataRequest: Opt<TableDataRequest<ManagementBreedersTableType>>,
        breederId: Opt<number>,
    ) => ({
        type: 'admin/GET_MANAGEMENT_BREEDERS_TABLE_DATA',
        payload: {tableDataRequest, breederId},
    }) as const,
    getManagementBreedersTableDataSuccess: (managementPage: Opt<ManagementBreedersPage>) => ({
        type: 'admin/GET_MANAGEMENT_BREEDERS_TABLE_DATA_SUCCESS',
        payload: managementPage,
    }) as const,
    setBreedersExtraColumns: (extraColumns: Array<ColumnHeader>) => ({
        type: 'admin/SET_BREEDERS_EXTRA_COLUMNS',
        payload: extraColumns,
    }) as const,
    setBreedersOptions: (options: Options<number>) => ({
        type: 'admin/SET_BREEDERS_OPTIONS',
        payload: options,
    }) as const,
    getBreedersOptions: (breederName: string) => ({
        type: 'admin/GET_BREEDERS_OPTIONS',
        payload: breederName,
    }) as const,
    getBreederDetail: (breederId: number) => ({
        type: 'admin/GET_BREEDER_DETAIL',
        payload: breederId,
    }) as const,
    getBreederDetailSuccess: (detail: Opt<UserProfileBreederRoleFarms>) => ({
        type: 'admin/GET_BREEDER_DETAIL_SUCCESS',
        payload: detail,
    }) as const,
    updateBreederDetail: (breederId: number) => ({
        type: 'admin/UPDATE_BREEDER_DETAIL',
        payload: breederId,
    }) as const,
    getBreedersFarms: (breederId: number) => ({
        type: 'admin/GET_BREEDERS_FARMS',
        payload: {breederId},
    }) as const,
    setBreedersFarms: (farms: Array<Farm>) => ({
        type: 'admin/SET_BREEDERS_FARMS',
        payload: farms,
    }) as const,
    removeBreederRolesForUser: (userProfileId: number, breederId: number) => ({
        type: 'admin/REMOVE_BREEDER_ROLES_FOR_USER',
        payload: {userProfileId, breederId},
    }) as const,
    removeUsersFromBreederRole: (roleId: number, breederId: number) => ({
        type: 'admin/REMOVE_USERS_FROM_BREEDER_ROLE',
        payload: {roleId, breederId},
    }) as const,
    searchManagementBreedersUsers: (searchText: string) => ({
        type: 'admin/SEARCH_MANAGEMENT_BREEDERS_USERS',
        payload: searchText,
    }) as const,
    setManagementBreedersUsers: (users: Array<User>) => ({
        type: 'admin/SET_MANAGEMENT_BREEDERS_USERS',
        payload: users,
    }) as const,
    searchManagementBreedersRoles: (searchText: string) => ({
        type: 'admin/SEARCH_MANAGEMENT_BREEDERS_ROLES',
        payload: searchText,
    }) as const,
    setManagementBreedersRoles: (roles: Array<BasicRole>) => ({
        type: 'admin/SET_MANAGEMENT_BREEDERS_ROLES',
        payload: roles,
    }) as const,
    setManagementSubjectModalVisible: (name: string | null) => ({
        type: 'admin/SET_MANAGEMENT_SUBJECT_MODAL_VISIBLE',
        payload: name,
    }) as const,
    createManagementBreeder: (breederId: number) => ({
        type: 'admin/CREATE_MANAGEMENT_BREEDER',
        payload: {breederId},
    }) as const,
    validateManagementBreeder: () => ({
        type: 'admin/VALIDATE_MANAGEMENT_BREEDER',
    }) as const,
    getManagementAssociationsOptions: (associationName: string) => ({
        type: 'admin/GET_MANAGEMENT_ASSOCIATIONS_OPTIONS',
        payload: associationName,
    }) as const,
    setManagementAssociationsOptions: (options: Options<number>) => ({
        type: 'admin/SET_MANAGEMENT_ASSOCIATIONS_OPTIONS',
        payload: options,
    }) as const,
    getManagementAssociationsTableData: (
        tableDataRequest: Opt<TableDataRequest<ManagementAssociationsTableType>>,
        associationId: Opt<number>,
    ) => ({
        type: 'admin/GET_MANAGEMENT_ASSOCIATIONS_TABLE_DATA',
        payload: {tableDataRequest, associationId},
    }) as const,
    getManagementAssociationsTableDataSuccess: (managementAssociationsPage: Opt<ManagementAssociationsPage>) => ({
        type: 'admin/GET_MANAGEMENT_ASSOCIATIONS_TABLE_DATA_SUCCESS',
        payload: managementAssociationsPage,
    }) as const,
    setManagementAssociationsExtraColumns: (extraColumns: Array<ColumnHeader>) => ({
        type: 'admin/SET_MANAGEMENT_ASSOCIATIONS_EXTRA_COLUMNS',
        payload: extraColumns,
    }) as const,
    removeAssociationRolesForUser: (userProfileId: number, associationId: number) => ({
        type: 'admin/REMOVE_ASSOCIATION_ROLES_FOR_USER',
        payload: {userProfileId, associationId},
    }) as const,
    removeUsersFromAssociationRole: (roleId: number, associationId: number) => ({
        type: 'admin/REMOVE_USERS_FROM_ASSOCIATION_ROLE',
        payload: {roleId, associationId},
    }) as const,
    searchManagementAssociationsUsers: (searchText: string, associationId: number) => ({
        type: 'admin/SEARCH_MANAGEMENT_ASSOCIATIONS_USERS',
        payload: {searchText, associationId},
    }) as const,
    setManagementAssociationsUsers: (users: Array<User>) => ({
        type: 'admin/SET_MANAGEMENT_ASSOCIATIONS_USERS',
        payload: users,
    }) as const,
    searchManagementAssociationsRoles: (searchText: string) => ({
        type: 'admin/SEARCH_MANAGEMENT_ASSOCIATIONS_ROLES',
        payload: searchText,
    }) as const,
    setManagementAssociationsRoles: (roles: Array<BasicRole>) => ({
        type: 'admin/SET_MANAGEMENT_ASSOCIATIONS_ROLES',
        payload: roles,
    }) as const,
    validateManagementAssociation: () => ({
        type: 'admin/VALIDATE_MANAGEMENT_ASSOCIATION',
    }) as const,
    createManagementAssociation: (associationId: number) => ({
        type: 'admin/CREATE_MANAGEMENT_ASSOCIATION',
        payload: {associationId},
    }) as const,
    getManagementAssociationDetail: (userRoleId: number) => ({
        type: 'admin/GET_MANAGEMENT_ASSOCIATION_DETAIL',
        payload: {userRoleId},
    }) as const,
    getManagementAssociationDetailSuccess: (detail: Opt<UserProfileAssociationRole>) => ({
        type: 'admin/GET_MANAGEMENT_ASSOCIATION_DETAIL_SUCCESS',
        payload: detail,
    }) as const,
    updateManagementAssociationDetail: (associationId: number) => ({
        type: 'admin/UPDATE_MANAGEMENT_ASSOCIATION_DETAIL',
        payload: {associationId},
    }) as const,
    getAuthDiscriminatorOptions: () => ({
        type: 'admin/GET_AUTH_DISCRIMINATOR_OPTIONS',
    }) as const,
    setAuthDiscriminators: (discriminators: Array<AuthDiscriminatorDetail>) => ({
        type: 'admin/SET_AUTH_DISCRIMINATOR_OPTIONS',
        payload: discriminators,
    }) as const,
    getManagementOtherSubjectsOptions: (otherSubjectName: string) => ({
        type: 'admin/GET_MANAGEMENT_OTHER_SUBJECTS_OPTIONS',
        payload: otherSubjectName,
    }) as const,
    setManagementOtherSubjectsOptions: (options: Options<number>) => ({
        type: 'admin/SET_MANAGEMENT_OTHER_SUBJECTS_OPTIONS',
        payload: options,
    }) as const,
    getManagementOtherSubjectsTableData: (
        tableDataRequest: Opt<TableDataRequest<ManagementOtherSubjectsTableType>>,
        otherSubjectId: Opt<number>,
    ) => ({
        type: 'admin/GET_MANAGEMENT_OTHER_SUBJECTS_TABLE_DATA',
        payload: {tableDataRequest, otherSubjectId},
    }) as const,
    getManagementOtherSubjectsTableDataSuccess: (managementOtherSubjectsPage: Opt<ManagementOtherSubjectsPage>) => ({
        type: 'admin/GET_MANAGEMENT_OTHER_SUBJECTS_TABLE_DATA_SUCCESS',
        payload: managementOtherSubjectsPage,
    }) as const,
    setManagementOtherSubjectsExtraColumns: (extraColumns: Array<ColumnHeader>) => ({
        type: 'admin/SET_MANAGEMENT_OTHER_SUBJECTS_EXTRA_COLUMNS',
        payload: extraColumns,
    }) as const,
    removeOtherSubjectRolesForUser: (userProfileId: number, otherSubjectId: number) => ({
        type: 'admin/REMOVE_OTHER_SUBJECT_ROLES_FOR_USER',
        payload: {userProfileId, otherSubjectId},
    }) as const,
    removeUsersFromOtherSubjectRole: (roleId: number, otherSubjectId: number) => ({
        type: 'admin/REMOVE_USERS_FROM_OTHER_SUBJECT_ROLE',
        payload: {roleId, otherSubjectId},
    }) as const,
    searchManagementOtherSubjectsUsers: (searchText: string, otherSubjectId: number) => ({
        type: 'admin/SEARCH_MANAGEMENT_OTHER_SUBJECTS_USERS',
        payload: {searchText, otherSubjectId},
    }) as const,
    setManagementOtherSubjectsUsers: (users: Array<User>) => ({
        type: 'admin/SET_MANAGEMENT_OTHER_SUBJECTS_USERS',
        payload: users,
    }) as const,
    searchManagementOtherSubjectsRoles: (searchText: string) => ({
        type: 'admin/SEARCH_MANAGEMENT_OTHER_SUBJECTS_ROLES',
        payload: searchText,
    }) as const,
    setManagementOtherSubjectsRoles: (roles: Array<BasicRole>) => ({
        type: 'admin/SET_MANAGEMENT_OTHER_SUBJECTS_ROLES',
        payload: roles,
    }) as const,
    validateManagementOtherSubject: () => ({
        type: 'admin/VALIDATE_MANAGEMENT_OTHER_SUBJECT',
    }) as const,
    createManagementOtherSubject: (otherSubjectId: number) => ({
        type: 'admin/CREATE_MANAGEMENT_OTHER_SUBJECT',
        payload: {otherSubjectId},
    }) as const,
    getManagementOtherSubjectDetail: (userRoleId: number) => ({
        type: 'admin/GET_MANAGEMENT_OTHER_SUBJECT_DETAIL',
        payload: {userRoleId},
    }) as const,
    getManagementOtherSubjectDetailSuccess: (detail: Opt<UserProfileOtherSubjectRole>) => ({
        type: 'admin/GET_MANAGEMENT_OTHER_SUBJECT_DETAIL_SUCCESS',
        payload: detail,
    }) as const,
    updateManagementOtherSubjectDetail: (otherSubjectId: number) => ({
        type: 'admin/UPDATE_MANAGEMENT_OTHER_SUBJECT_DETAIL',
        payload: {otherSubjectId},
    }) as const,

    getManagementOrganizationsOptions: (organizationName: string) => ({
        type: 'admin/GET_MANAGEMENT_ORGANIZATIONS_OPTIONS',
        payload: organizationName,
    }) as const,
    setManagementOrganizationsOptions: (options: Options<number>) => ({
        type: 'admin/SET_MANAGEMENT_ORGANIZATIONS_OPTIONS',
        payload: options,
    }) as const,
    getManagementOrganizationsTableData: (
        tableDataRequest: Opt<TableDataRequest<ManagementOrganizationsTableType>>,
        organizationId: number | undefined,
    ) => ({
        type: 'admin/GET_MANAGEMENT_ORGANIZATIONS_TABLE_DATA',
        payload: {tableDataRequest, organizationId},
    }) as const,
    getManagementOrganizationsTableDataSuccess: (managementOrganizationsPage: Opt<ManagementOrganizationsPage>) => ({
        type: 'admin/GET_MANAGEMENT_ORGANIZATIONS_TABLE_DATA_SUCCESS',
        payload: managementOrganizationsPage,
    }) as const,
    setManagementOrganizationsExtraColumns: (extraColumns: Array<ColumnHeader>) => ({
        type: 'admin/SET_MANAGEMENT_ORGANIZATIONS_EXTRA_COLUMNS',
        payload: extraColumns,
    }) as const,
    removeOrganizationRolesForUser: (userProfileId: number, organizationId: number) => ({
        type: 'admin/REMOVE_ORGANIZATION_ROLES_FOR_USER',
        payload: {userProfileId, organizationId},
    }) as const,
    removeUsersFromOrganizationRole: (roleId: number, organizationId: number) => ({
        type: 'admin/REMOVE_USERS_FROM_ORGANIZATION_ROLE',
        payload: {roleId, organizationId},
    }) as const,
    searchManagementOrganizationsUsers: (searchText: string, organizationId: number) => ({
        type: 'admin/SEARCH_MANAGEMENT_ORGANIZATIONS_USERS',
        payload: {searchText, organizationId},
    }) as const,
    setManagementOrganizationsUsers: (users: Array<User>) => ({
        type: 'admin/SET_MANAGEMENT_ORGANIZATIONS_USERS',
        payload: users,
    }) as const,
    searchManagementOrganizationsRoles: (searchText: string) => ({
        type: 'admin/SEARCH_MANAGEMENT_ORGANIZATIONS_ROLES',
        payload: searchText,
    }) as const,
    setManagementOrganizationsRoles: (roles: Array<BasicRole>) => ({
        type: 'admin/SET_MANAGEMENT_ORGANIZATIONS_ROLES',
        payload: roles,
    }) as const,
    validateManagementOrganization: () => ({
        type: 'admin/VALIDATE_MANAGEMENT_ORGANIZATION',
    }) as const,
    createManagementOrganization: (organizationId: number) => ({
        type: 'admin/CREATE_MANAGEMENT_ORGANIZATION',
        payload: {organizationId},
    }) as const,
    getManagementOrganizationDetail: (userRoleId: number) => ({
        type: 'admin/GET_MANAGEMENT_ORGANIZATION_DETAIL',
        payload: {userRoleId},
    }) as const,
    getManagementOrganizationDetailSuccess: (detail: Opt<UserProfileOrganizationRoleFarmsCenters>) => ({
        type: 'admin/GET_MANAGEMENT_ORGANIZATION_DETAIL_SUCCESS',
        payload: detail,
    }) as const,
    updateManagementOrganizationDetail: (organizationId: number) => ({
        type: 'admin/UPDATE_MANAGEMENT_ORGANIZATION_DETAIL',
        payload: {organizationId},
    }) as const,
    getManagementOrganizationFarms: (organizationId: number) => ({
        type: 'admin/GET_MANAGEMENT_ORGANIZATION_FARMS',
        payload: {organizationId},
    }) as const,
    setManagementOrganizationFarms: (farms: Array<Farm>) => ({
        type: 'admin/SET_MANAGEMENT_ORGANIZATION_FARMS',
        payload: farms,
    }) as const,
    getManagementOrganizationCenters: (organizationId: number) => ({
        type: 'admin/GET_MANAGEMENT_ORGANIZATION_CENTERS',
        payload: {organizationId},
    }) as const,
    setManagementOrganizationCenters: (organizations: Array<OrganizationCenter>) => ({
        type: 'admin/SET_MANAGEMENT_ORGANIZATION_CENTERS',
        payload: organizations,
    }) as const,
};

/** Redux action type alias. */
export type GetBreedersFarmsAction = ReturnType<typeof adminAction.getBreedersFarms>;
/** Redux action type alias. */
export type GetAuthDiscriminatorOptionsAction = ReturnType<typeof adminAction.getAuthDiscriminatorOptions>;
/** Redux action type alias. */
export type SetAuthDiscriminatorsAction = ReturnType<typeof adminAction.setAuthDiscriminators>;
/** Redux action type alias. */
export type GetBreederDetailAction = ReturnType<typeof adminAction.getBreederDetail>;
/** Redux action type alias. */
export type UpdateBreederDetailAction = ReturnType<typeof adminAction.updateBreederDetail>;
/** Redux action type alias. */
export type GetBreederDetailSuccessAction = ReturnType<typeof adminAction.getBreederDetailSuccess>;
/** Redux action type alias. */
export type SetBreedersFarmsAction = ReturnType<typeof adminAction.setBreedersFarms>;
/** Redux action type alias. */
export type SetBreedersOptionsAction = ReturnType<typeof adminAction.setBreedersOptions>;
/** Redux action type alias. */
export type GetBreedersOptionsAction = ReturnType<typeof adminAction.getBreedersOptions>;
/** Redux action type alias. */
export type GetManagementBreedersTableDataAction = ReturnType<typeof adminAction.getManagementBreedersTableData>;
/** Redux action type alias. */
export type GetManagementBreedersTableDataSuccessAction =
    ReturnType<typeof adminAction.getManagementBreedersTableDataSuccess>;
/** Redux action type alias. */
export type SetBreedersExtraColumnsAction = ReturnType<typeof adminAction.setBreedersExtraColumns>;
/** Redux action type alias. */
export type GetAppInfoAction = ReturnType<typeof adminAction.getAppInfo>;
/** Redux action type alias. */
export type GetUserContactsAction = ReturnType<typeof adminAction.getUserContacts>;
/** Redux action type alias. */
export type UpdateBreederAction = ReturnType<typeof adminAction.updateBreeder>;
/** Redux action type alias. */
export type CreateBreederAction = ReturnType<typeof adminAction.createBreeder>;
/** Redux action type alias. */
export type UpdateOtherSubjectsAction = ReturnType<typeof adminAction.updateOtherSubjects>;
/** Redux action type alias. */
export type CreateOrganizationAction = ReturnType<typeof adminAction.createOrganization>;
/** Redux action type alias. */
export type UpdateOrganizationAction = ReturnType<typeof adminAction.updateOrganization>;
/** Redux action type alias. */
export type CreateOtherSubjectsAction = ReturnType<typeof adminAction.createOtherSubjects>;
/** Redux action type alias. */
export type DeleteUserContactAction = ReturnType<typeof adminAction.deleteUserContact>;
/** Redux action type alias. */
export type UpdateUserContactAction = ReturnType<typeof adminAction.updateUserContact>;
/** Redux action type alias. */
export type CreateUserContactAction = ReturnType<typeof adminAction.createUserContact>;
/** Redux action type alias. */
export type CreateAssociationRolesAction = ReturnType<typeof adminAction.createAssociationRoles>;
/** Redux action type alias. */
export type UpdateAssociationRolesAction = ReturnType<typeof adminAction.updateAssociationRoles>;
/** Redux action type alias. */
export type GetUserContactsSuccessAction = ReturnType<typeof adminAction.getUserContactsSuccess>;
/** Redux action type alias. */
export type GetAssociationsAction = ReturnType<typeof adminAction.getAssociations>;
/** Redux action type alias. */
export type GetAssociationsSuccessAction = ReturnType<typeof adminAction.getAssociationsSuccess>;
/** Redux action type alias. */
export type GetOtherSubjectRolesAction = ReturnType<typeof adminAction.getOtherSubjectRoles>;
/** Redux action type alias. */
export type GetOtherSubjectRolesSuccessAction = ReturnType<typeof adminAction.getOtherSubjectRolesSuccess>;
/** Redux action type alias. */
export type GetUserAction = ReturnType<typeof adminAction.getUser>;
/** Redux action type alias. */
export type GetUserSuccessAction = ReturnType<typeof adminAction.getUserSuccess>;
/** Redux action type alias. */
export type ClearUserAction = ReturnType<typeof adminAction.clearUser>;
/** Redux action type alias. */
export type GetMemberOfSubjectsAction = ReturnType<typeof adminAction.getMemberOfSubjects>;
/** Redux action type alias. */
export type GetMemberOfSubjectsSuccessAction = ReturnType<typeof adminAction.getMemberOfSubjectsSuccess>;
/** Redux action type alias. */
export type CreateUserMembershipAction = ReturnType<typeof adminAction.createUserMembership>;
/** Redux action type alias. */
export type UpdateUserMembershipAction = ReturnType<typeof adminAction.updateUserMembership>;
/** Redux action type alias. */
export type GetUsersSuccessAction = ReturnType<typeof adminAction.getUsersSuccess>;
/** Redux action type alias. */
export type GetAppInfoSuccessAction = ReturnType<typeof adminAction.getAppInfoSuccess>;
/** Redux action type alias. */
export type UpdatePermissionAction = ReturnType<typeof adminAction.updatePermission>;
/** Redux action type alias. */
export type ValidateRoleAction = ReturnType<typeof adminAction.validateRole>;
/** Redux action type alias. */
export type ChangeSubordinateRoleAction = ReturnType<typeof adminAction.changeSubordinateRole>;
/** Redux action type alias. */
export type DeletePermissionAction = ReturnType<typeof adminAction.deletePermission>;
/** Redux action type alias. */
export type UpdateRoleAction = ReturnType<typeof adminAction.updateRole>;
/** Redux action type alias. */
export type ValidatePermissionAction = ReturnType<typeof adminAction.validatePermission>;
/** Redux action type alias. */
export type GetRolesAction = ReturnType<typeof adminAction.getRoles>;
/** Redux action type alias. */
export type GetRolesSuccessAction = ReturnType<typeof adminAction.getRolesSuccess>;
/** Redux action type alias. */
export type DeleteRoleAction = ReturnType<typeof adminAction.deleteRole>;
/** Redux action type alias. */
export type GetSubordinateAssignedRolesAction = ReturnType<typeof adminAction.getSubordinateAssignedRoles>;
/** Redux action type alias. */
export type GetSubordinateAssignedRolesSuccessAction =
    ReturnType<typeof adminAction.getSubordinateAssignedRolesSuccess>;
/** Redux action type alias. */
export type GetSubordinateUnassignedRolesAction = ReturnType<typeof adminAction.getSubordinateUnassignedRoles>;
/** Redux action type alias. */
export type GetSubordinateUnassignedRolesSuccessAction =
    ReturnType<typeof adminAction.getSubordinateUnassignedRolesSuccess>;
/** Redux action type alias. */
export type GetRoleAction = ReturnType<typeof adminAction.getRole>;
/** Redux action type alias. */
export type GetRoleSuccessAction = ReturnType<typeof adminAction.getRoleSuccess>;
/** Redux action type alias. */
export type ClearRoleAction = ReturnType<typeof adminAction.clearRole>;
/** Redux action type alias. */
export type GetRolePermissionsAction = ReturnType<typeof adminAction.getRolePermissions>;
/** Redux action type alias. */
export type GetRolePermissionsSuccessAction = ReturnType<typeof adminAction.getRolePermissionsSuccess>;
/** Redux action type alias. */
export type GetRoleUnassignedPermissionsAction = ReturnType<typeof adminAction.getRoleUnassignedPermissions>;
/** Redux action type alias. */
export type GetRoleUnassignedPermissionsSuccessAction =
    ReturnType<typeof adminAction.getRoleUnassignedPermissionsSuccess>;
/** Redux action type alias. */
export type RemovePermissionFromRoleAction = ReturnType<typeof adminAction.removePermissionFromRole>;
/** Redux action type alias. */
export type AddPermissionToRoleAction = ReturnType<typeof adminAction.addPermissionToRole>;
/** Redux action type alias. */
export type GetPermissionDetailsAction = ReturnType<typeof adminAction.getPermissionDetails>;
/** Redux action type alias. */
export type GetPermissionDetailsSuccessAction = ReturnType<typeof adminAction.getPermissionDetailsSuccess>;
/** Redux action type alias. */
export type SetEditedPermissionIdAction = ReturnType<typeof adminAction.setEditedPermissionId>;
/** Redux action type alias. */
export type SetPermissionCreateVisibilityAction = ReturnType<typeof adminAction.setPermissionCreateVisibility>;
/** Redux action type alias. */
export type CreatePermissionAction = ReturnType<typeof adminAction.createPermission>;
/** Redux action type alias. */
export type CreateRoleAction = ReturnType<typeof adminAction.createRole>;
/** Redux action type alias. */
export type SetRoleCreateModalVisibilityAction = ReturnType<typeof adminAction.setRoleCreateModalVisibility>;
/** Redux action type alias. */
export type SetEditedRoleIdAction = ReturnType<typeof adminAction.setEditedRoleId>;
/** Redux action type alias. */
export type GetOrganizationsAction = ReturnType<typeof adminAction.getOrganizations>;
/** Redux action type alias. */
export type GetOrganizationsActionSuccess = ReturnType<typeof adminAction.getOrganizationsSuccess>;
/** Redux action type alias. */
export type GetUserBreedersAction = ReturnType<typeof adminAction.getUserBreeders>;
/** Redux action type alias. */
export type GetUserBreedersActionSuccess = ReturnType<typeof adminAction.getUserBreedersSuccess>;
/** Redux action type alias. */
export type SetLoadingAction = ReturnType<typeof adminAction.setLoading>;
/** Redux action type alias. */
export type RemoveBreederRolesForUserAction = ReturnType<typeof adminAction.removeBreederRolesForUser>;
/** Redux action type alias. */
export type RemoveUsersFromBreederRoleAction = ReturnType<typeof adminAction.removeUsersFromBreederRole>;
/** Redux action type alias. */
export type SearchManagementBreedersUsersAction = ReturnType<typeof adminAction.searchManagementBreedersUsers>;
/** Redux action type alias. */
export type SetManagementBreedersUsersAction = ReturnType<typeof adminAction.setManagementBreedersUsers>;
/** Redux action type alias. */
export type SearchManagementBreedersRolesAction = ReturnType<typeof adminAction.searchManagementBreedersRoles>;
/** Redux action type alias. */
export type SetManagementBreedersRolesAction = ReturnType<typeof adminAction.setManagementBreedersRoles>;
/** Redux action type alias. */
export type SetManagementSubjectModalVisibleAction = ReturnType<typeof adminAction.setManagementSubjectModalVisible>;
/** Redux action type alias. */
export type CreateManagementBreederAction = ReturnType<typeof adminAction.createManagementBreeder>;
/** Redux action type alias. */
export type ValidateManagementBreederAction = ReturnType<typeof adminAction.validateManagementBreeder>;
/** Redux action type alias. */
export type GetManagementAssociationsOptionsAction = ReturnType<typeof adminAction.getManagementAssociationsOptions>;
/** Redux action type alias. */
export type SetManagementAssociationsOptionsAction = ReturnType<typeof adminAction.setManagementAssociationsOptions>;
/** Redux action type alias. */
export type GetManagementAssociationsTableDataAction =
    ReturnType<typeof adminAction.getManagementAssociationsTableData>;
/** Redux action type alias. */
export type GetManagementAssociationsTableDataSuccessAction =
    ReturnType<typeof adminAction.getManagementAssociationsTableDataSuccess>;
/** Redux action type alias. */
export type SetManagementAssociationsExtraColumnsAction =
    ReturnType<typeof adminAction.setManagementAssociationsExtraColumns>;
/** Redux action type alias. */
export type RemoveAssociationRolesForUserAction = ReturnType<typeof adminAction.removeAssociationRolesForUser>;
/** Redux action type alias. */
export type RemoveUsersFromAssociationRoleAction = ReturnType<typeof adminAction.removeUsersFromAssociationRole>;
/** Redux action type alias. */
export type SearchManagementAssociationsUsersAction = ReturnType<typeof adminAction.searchManagementAssociationsUsers>;
/** Redux action type alias. */
export type SetManagementAssociationsUsersAction = ReturnType<typeof adminAction.setManagementAssociationsUsers>;
/** Redux action type alias. */
export type SearchManagementAssociationsRolesAction = ReturnType<typeof adminAction.searchManagementAssociationsRoles>;
/** Redux action type alias. */
export type SetManagementAssociationsRoles = ReturnType<typeof adminAction.setManagementAssociationsRoles>;
/** Redux action type alias. */
export type ValidateManagementAssociationAction = ReturnType<typeof adminAction.validateManagementAssociation>;
/** Redux action type alias. */
export type CreateManagementAssociationAction = ReturnType<typeof adminAction.createManagementAssociation>;
/** Redux action type alias. */
export type GetManagementAssociationDetailAction = ReturnType<typeof adminAction.getManagementAssociationDetail>;
/** Redux action type alias. */
export type GetManagementAssociationDetailSuccessAction =
    ReturnType<typeof adminAction.getManagementAssociationDetailSuccess>;
/** Redux action type alias. */
export type UpdateManagementAssociationDetailAction = ReturnType<typeof adminAction.updateManagementAssociationDetail>;

/** Redux action type alias. */
export type GetManagementOtherSubjectsOptionsAction = ReturnType<typeof adminAction.getManagementOtherSubjectsOptions>;
/** Redux action type alias. */
export type SetManagementOtherSubjectsOptionsAction = ReturnType<typeof adminAction.setManagementOtherSubjectsOptions>;
/** Redux action type alias. */
export type GetManagementOtherSubjectsTableDataAction =
    ReturnType<typeof adminAction.getManagementOtherSubjectsTableData>;
/** Redux action type alias. */
export type GetManagementOtherSubjectsTableDataSuccessAction =
    ReturnType<typeof adminAction.getManagementOtherSubjectsTableDataSuccess>;
/** Redux action type alias. */
export type SetManagementOtherSubjectsExtraColumnsAction =
    ReturnType<typeof adminAction.setManagementOtherSubjectsExtraColumns>;
/** Redux action type alias. */
export type RemoveOtherSubjectRolesForUserAction = ReturnType<typeof adminAction.removeOtherSubjectRolesForUser>;
/** Redux action type alias. */
export type RemoveUsersFromOtherSubjectRoleAction = ReturnType<typeof adminAction.removeUsersFromOtherSubjectRole>;
/** Redux action type alias. */
export type SearchManagementOtherSubjectsUsersAction =
    ReturnType<typeof adminAction.searchManagementOtherSubjectsUsers>;
/** Redux action type alias. */
export type SetManagementOtherSubjectsUsersAction = ReturnType<typeof adminAction.setManagementOtherSubjectsUsers>;
/** Redux action type alias. */
export type SearchManagementOtherSubjectsRolesAction =
    ReturnType<typeof adminAction.searchManagementOtherSubjectsRoles>;
/** Redux action type alias. */
export type SetManagementOtherSubjectsRoles = ReturnType<typeof adminAction.setManagementOtherSubjectsRoles>;
/** Redux action type alias. */
export type ValidateManagementOtherSubjectAction = ReturnType<typeof adminAction.validateManagementOtherSubject>;
/** Redux action type alias. */
export type CreateManagementOtherSubjectAction = ReturnType<typeof adminAction.createManagementOtherSubject>;
/** Redux action type alias. */
export type GetManagementOtherSubjectDetailAction = ReturnType<typeof adminAction.getManagementOtherSubjectDetail>;
/** Redux action type alias. */
export type GetManagementOtherSubjectDetailSuccessAction =
    ReturnType<typeof adminAction.getManagementOtherSubjectDetailSuccess>;
/** Redux action type alias. */
export type UpdateManagementOtherSubjectDetailAction =
    ReturnType<typeof adminAction.updateManagementOtherSubjectDetail>;

/** Redux action type alias. */
export type GetManagementOrganizationsOptionsAction = ReturnType<typeof adminAction.getManagementOrganizationsOptions>;
/** Redux action type alias. */
export type SetManagementOrganizationsOptionsAction = ReturnType<typeof adminAction.setManagementOrganizationsOptions>;
/** Redux action type alias. */
export type GetManagementOrganizationsTableDataAction =
    ReturnType<typeof adminAction.getManagementOrganizationsTableData>;
/** Redux action type alias. */
export type GetManagementOrganizationsTableDataSuccessAction =
    ReturnType<typeof adminAction.getManagementOrganizationsTableDataSuccess>;
/** Redux action type alias. */
export type SetManagementOrganizationsExtraColumnsAction =
    ReturnType<typeof adminAction.setManagementOrganizationsExtraColumns>;
/** Redux action type alias. */
export type RemoveOrganizationRolesForUserAction = ReturnType<typeof adminAction.removeOrganizationRolesForUser>;
/** Redux action type alias. */
export type RemoveUsersFromOrganizationRoleAction = ReturnType<typeof adminAction.removeUsersFromOrganizationRole>;
/** Redux action type alias. */
export type SearchManagementOrganizationsUsersAction =
    ReturnType<typeof adminAction.searchManagementOrganizationsUsers>;
/** Redux action type alias. */
export type SetManagementOrganizationsUsersAction = ReturnType<typeof adminAction.setManagementOrganizationsUsers>;
/** Redux action type alias. */
export type SearchManagementOrganizationsRolesAction =
    ReturnType<typeof adminAction.searchManagementOrganizationsRoles>;
/** Redux action type alias. */
export type SetManagementOrganizationsRoles = ReturnType<typeof adminAction.setManagementOrganizationsRoles>;
/** Redux action type alias. */
export type ValidateManagementOrganizationAction = ReturnType<typeof adminAction.validateManagementOrganization>;
/** Redux action type alias. */
export type CreateManagementOrganizationAction = ReturnType<typeof adminAction.createManagementOrganization>;
/** Redux action type alias. */
export type GetManagementOrganizationDetailAction = ReturnType<typeof adminAction.getManagementOrganizationDetail>;
/** Redux action type alias. */
export type GetManagementOrganizationDetailSuccessAction =
    ReturnType<typeof adminAction.getManagementOrganizationDetailSuccess>;
/** Redux action type alias. */
export type UpdateManagementOrganizationDetailAction =
    ReturnType<typeof adminAction.updateManagementOrganizationDetail>;
/** Redux action type alias. */
export type GetManagementOrganizationFarmsAction = ReturnType<typeof adminAction.getManagementOrganizationFarms>;
/** Redux action type alias. */
export type GetManagementOrganizationCentersAction = ReturnType<typeof adminAction.getManagementOrganizationCenters>;
/** Redux action type alias. */
export type SetManagementOrganizationFarmsAction = ReturnType<typeof adminAction.setManagementOrganizationFarms>;
/** Redux action type alias. */
export type SetManagementOrganizationCentersAction = ReturnType<typeof adminAction.setManagementOrganizationCenters>;

/** Redux action type alias. */
export type AdminAction =
    | GetAppInfoAction
    | GetManagementBreedersTableDataSuccessAction
    | GetBreederDetailAction
    | UpdateBreederDetailAction
    | GetAuthDiscriminatorOptionsAction
    | SetAuthDiscriminatorsAction
    | SetBreedersExtraColumnsAction
    | GetBreederDetailSuccessAction
    | GetBreedersFarmsAction
    | SetBreedersFarmsAction
    | GetManagementBreedersTableDataAction
    | GetBreedersOptionsAction
    | SetBreedersOptionsAction
    | CreateBreederAction
    | UpdateBreederAction
    | CreateOrganizationAction
    | UpdateOrganizationAction
    | GetOrganizationsAction
    | GetOrganizationsActionSuccess
    | UpdateOtherSubjectsAction
    | CreateOtherSubjectsAction
    | UpdateAssociationRolesAction
    | CreateAssociationRolesAction
    | GetMemberOfSubjectsAction
    | CreateUserContactAction
    | GetAssociationsAction
    | GetAssociationsSuccessAction
    | GetOtherSubjectRolesAction
    | GetOtherSubjectRolesSuccessAction
    | DeleteUserContactAction
    | UpdateUserContactAction
    | GetUserContactsAction
    | GetUserContactsSuccessAction
    | GetMemberOfSubjectsSuccessAction
    | UpdateUserMembershipAction
    | GetUserAction
    | GetUserSuccessAction
    | GetUsersSuccessAction
    | GetAppInfoSuccessAction
    | SetEditedRoleIdAction
    | CreatePermissionAction
    | SetRoleCreateModalVisibilityAction
    | UpdateRoleAction
    | UpdatePermissionAction
    | GetSubordinateAssignedRolesAction
    | GetSubordinateAssignedRolesSuccessAction
    | GetSubordinateUnassignedRolesAction
    | GetSubordinateUnassignedRolesSuccessAction
    | ChangeSubordinateRoleAction
    | DeletePermissionAction
    | SetEditedPermissionIdAction
    | SetPermissionCreateVisibilityAction
    | ValidatePermissionAction
    | GetRoleSuccessAction
    | GetRolesAction
    | GetRolesSuccessAction
    | GetRolePermissionsAction
    | GetRolePermissionsSuccessAction
    | DeleteRoleAction
    | GetRoleAction
    | GetRoleUnassignedPermissionsAction
    | GetRoleUnassignedPermissionsSuccessAction
    | ValidateRoleAction
    | RemovePermissionFromRoleAction
    | AddPermissionToRoleAction
    | GetPermissionDetailsAction
    | CreateRoleAction
    | GetPermissionDetailsSuccessAction
    | GetUserBreedersAction
    | GetUserBreedersActionSuccess
    | ClearUserAction
    | ClearRoleAction
    | SetLoadingAction
    | RemoveBreederRolesForUserAction
    | RemoveUsersFromBreederRoleAction
    | SearchManagementBreedersUsersAction
    | SetManagementBreedersUsersAction
    | SearchManagementBreedersRolesAction
    | SetManagementBreedersRolesAction
    | SetManagementSubjectModalVisibleAction
    | CreateManagementBreederAction
    | ValidateManagementBreederAction
    | GetManagementAssociationsOptionsAction
    | SetManagementAssociationsOptionsAction
    | GetManagementAssociationsTableDataAction
    | GetManagementAssociationsTableDataSuccessAction
    | SetManagementAssociationsExtraColumnsAction
    | RemoveAssociationRolesForUserAction
    | RemoveUsersFromAssociationRoleAction
    | SearchManagementAssociationsUsersAction
    | SetManagementAssociationsUsersAction
    | SearchManagementAssociationsRolesAction
    | SetManagementAssociationsRoles
    | ValidateManagementAssociationAction
    | CreateManagementAssociationAction
    | GetManagementAssociationDetailAction
    | GetManagementAssociationDetailSuccessAction
    | UpdateManagementAssociationDetailAction
    | GetManagementOtherSubjectsOptionsAction
    | SetManagementOtherSubjectsOptionsAction
    | GetManagementOtherSubjectsTableDataAction
    | GetManagementOtherSubjectsTableDataSuccessAction
    | SetManagementOtherSubjectsExtraColumnsAction
    | RemoveOtherSubjectRolesForUserAction
    | RemoveUsersFromOtherSubjectRoleAction
    | SearchManagementOtherSubjectsUsersAction
    | SetManagementOtherSubjectsUsersAction
    | SearchManagementOtherSubjectsRolesAction
    | SetManagementOtherSubjectsRoles
    | ValidateManagementOtherSubjectAction
    | CreateManagementOtherSubjectAction
    | GetManagementOtherSubjectDetailAction
    | GetManagementOtherSubjectDetailSuccessAction
    | UpdateManagementOtherSubjectDetailAction
    | GetManagementOrganizationsOptionsAction
    | SetManagementOrganizationsOptionsAction
    | GetManagementOrganizationsTableDataAction
    | GetManagementOrganizationsTableDataSuccessAction
    | SetManagementOrganizationsExtraColumnsAction
    | RemoveOrganizationRolesForUserAction
    | RemoveUsersFromOrganizationRoleAction
    | SearchManagementOrganizationsUsersAction
    | SetManagementOrganizationsUsersAction
    | SearchManagementOrganizationsRolesAction
    | SetManagementOrganizationsRoles
    | ValidateManagementOrganizationAction
    | CreateManagementOrganizationAction
    | GetManagementOrganizationDetailAction
    | GetManagementOrganizationDetailSuccessAction
    | UpdateManagementOrganizationDetailAction
    | GetManagementOrganizationFarmsAction
    | GetManagementOrganizationCentersAction
    | SetManagementOrganizationFarmsAction
    | SetManagementOrganizationCentersAction
    ;
